import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetRealEstateTeamOptionsQuery,
  useGetRealEstateTeamsQuery,
} from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import {
  createRealEstateTeamApi,
  deactivateRealEstateTeamApi,
  updateRealEstateTeamApi,
} from "../../../services/realEstateTeam";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const EMPTY = { _id: "", name: "", managerId: "", memberIds: [], notes: "" };

// Also used by the Daily Report Summary to create the first team in place.
export const TeamForm = ({ show, onHide, initial, options }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  React.useEffect(() => {
    setForm(initial || EMPTY);
    setSearch("");
  }, [initial, show]);

  // One list of every active staff member. "Member" ticks who is in the team;
  // "Team head" ticks the one person who leads it (any role). A person may
  // be in several teams.
  const staff = options?.employees || [];
  const inTeam = (id) => id === form.managerId || form.memberIds.includes(id);
  const toggleMember = (id) => {
    if (id === form.managerId) {
      setForm({ ...form, managerId: "" });
    } else if (form.memberIds.includes(id)) {
      setForm({ ...form, memberIds: form.memberIds.filter((x) => x !== id) });
    } else {
      setForm({ ...form, memberIds: [...form.memberIds, id] });
    }
  };
  const makeHead = (id) => {
    if (id === form.managerId) return;
    const memberIds = form.memberIds.filter((x) => x !== id);
    if (form.managerId) memberIds.push(form.managerId); // old head stays a member
    setForm({ ...form, managerId: id, memberIds });
  };
  const q = search.trim().toLowerCase();
  const visibleStaff = staff
    .filter(
      (e) =>
        !q ||
        String(e.name || "")
          .toLowerCase()
          .includes(q) ||
        String(e.role || "")
          .toLowerCase()
          .includes(q),
    )
    // Team first (head on top), then everyone else alphabetically.
    .sort(
      (a, b) =>
        (b._id === form.managerId) - (a._id === form.managerId) ||
        inTeam(b._id) - inTeam(a._id) ||
        String(a.name).localeCompare(String(b.name)),
    );
  const head = staff.find((e) => e._id === form.managerId);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.managerId) {
      Swal.fire({
        icon: "warning",
        title: "Pick a team head",
        text: "Tick “Team head” next to the person who leads this team.",
      });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        managerId: form.managerId,
        memberIds: form.memberIds,
        notes: form.notes,
      };
      if (form._id) await updateRealEstateTeamApi(form._id, payload);
      else await createRealEstateTeamApi(payload);
      dispatch(appApi.util.invalidateTags(["RealEstateTeams"]));
      if (!form._id) {
        Swal.fire({
          icon: "success",
          title: "Team created",
          timer: 1400,
          showConfirmButton: false,
        });
      }
      onHide();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Unable to save team",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{form._id ? "Edit team" : "New team"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="space-y-4">
          <div>
            <label className={labelClass}>Team name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. North Zone Sales"
              required
            />
          </div>
          <div>
            <div className="mb-1 flex flex-wrap items-end justify-between gap-2">
              <label className={`${labelClass} mb-0`}>
                Team ({form.memberIds.length + (form.managerId ? 1 : 0)}) —
                head:{" "}
                <span className="normal-case text-gray-800">
                  {head?.name || "not chosen"}
                </span>
              </label>
              <input
                className={`${inputClass} max-w-xs`}
                placeholder="Search name or role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="w-20 px-3 py-2 text-center">Member</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Role</th>
                    <th className="w-24 px-3 py-2 text-center">Team head</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {!options && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-6 text-center text-gray-400"
                      >
                        Loading staff…
                      </td>
                    </tr>
                  )}
                  {options && visibleStaff.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-6 text-center text-gray-400"
                      >
                        No staff match “{search}”.
                      </td>
                    </tr>
                  )}
                  {visibleStaff.map((e) => {
                    const isHead = e._id === form.managerId;
                    return (
                      <tr
                        key={e._id}
                        className={
                          isHead
                            ? "bg-amber-50"
                            : inTeam(e._id)
                              ? "bg-gray-50/60"
                              : ""
                        }
                      >
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-amber-500"
                            checked={inTeam(e._id)}
                            onChange={() => toggleMember(e._id)}
                            aria-label={`${e.name} is a member`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {e.name}
                          {isHead && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              TEAM HEAD
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {e.role || "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-amber-600"
                            checked={isHead}
                            onChange={() =>
                              isHead
                                ? setForm({
                                    ...form,
                                    managerId: "",
                                    memberIds: [...form.memberIds, e._id],
                                  })
                                : makeHead(e._id)
                            }
                            aria-label={`${e.name} is the team head`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Tick “Member” for everyone in the team and “Team head” for the one
              person who leads it — any staff member can be the head. The head
              sees the members' reports and can add reports for them. A person
              can be in more than one team.
            </p>
          </div>
          <div>
            <label className={labelClass}>Notes (optional)</label>
            <textarea
              className={inputClass}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save team"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

const Teams = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const { data, isFetching } = useGetRealEstateTeamsQuery({});
  const { data: options } = useGetRealEstateTeamOptionsQuery(undefined, {
    skip: !isSuperAdmin,
  });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const teams = useMemo(() => data?.teams || [], [data]);

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (team) => {
    setEditing({
      _id: team._id,
      name: team.name,
      managerId: team.manager?._id || "",
      memberIds: (team.members || []).map((m) => m._id),
      notes: team.notes || "",
    });
    setShowForm(true);
  };
  const deactivate = async (team) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: `Deactivate "${team.name}"?`,
      text: "The team head stops seeing these members. Existing reports are kept.",
      showCancelButton: true,
      confirmButtonText: "Deactivate",
    });
    if (!ok.isConfirmed) return;
    try {
      await deactivateRealEstateTeamApi(team._id);
      dispatch(appApi.util.invalidateTags(["RealEstateTeams"]));
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={isSuperAdmin ? "Team Real Estate" : "My Team"}
        motherMenu="Workforce"
      />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Team Real Estate
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {isSuperAdmin
                  ? "Create teams of any staff and tick who the team head is."
                  : "Your team members. You can view their reports and add new reports on their behalf."}
              </p>
            </div>
            <div className="flex gap-2">
              {/* <button
                type="button"
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-amber-400"
                onClick={() => navigate("/real-estate-team/club-report")}
              >
                Combined report
              </button> */}
              {isSuperAdmin && (
                <button
                  type="button"
                  className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"
                  onClick={openNew}
                >
                  + New team
                </button>
              )}
            </div>
          </div>
        </section>

        {isFetching && !teams.length ? (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            Loading…
          </section>
        ) : !teams.length ? (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            {isSuperAdmin
              ? "No teams yet. Create the first one."
              : "You have not been assigned a team yet."}
          </section>
        ) : (
          teams.map((team) => (
            <section key={team._id} className={panel}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {team.name}
                    {!team.active && (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">
                        INACTIVE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Team head: <b>{team.manager?.name || "—"}</b> ·{" "}
                    {team.memberCount} member{team.memberCount === 1 ? "" : "s"}
                  </div>
                </div>
                {isSuperAdmin && team.active && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400"
                      onClick={() => openEdit(team)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      onClick={() => deactivate(team)}
                    >
                      Deactivate
                    </button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-left">Phone</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(team.members || []).map((m) => (
                      <tr key={m._id}>
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {m.name}
                          {m.status === false && (
                            <span className="ml-2 text-xs text-red-500">
                              (inactive)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {m.phone || "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="mr-2 rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400"
                            onClick={() =>
                              navigate(`/staff-reporting/employee/${m._id}`)
                            }
                          >
                            Reports
                          </button>
                          {!isSuperAdmin && m.status !== false && (
                            <button
                              type="button"
                              className="rounded-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-amber-400"
                              onClick={() =>
                                navigate(
                                  `/staff-reporting/create?employeeId=${m._id}`,
                                )
                              }
                            >
                              + Add report
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </div>

      {isSuperAdmin && (
        <TeamForm
          show={showForm}
          onHide={() => setShowForm(false)}
          initial={editing}
          options={options}
        />
      )}
    </>
  );
};

export default Teams;
